import React, { useState } from "react";
import { AlertCircle, CheckCircle, Loader, Send } from "lucide-react";
import { submitLead, validateLead } from "../../utils/lead";
import { trackFormLead } from "../../utils/ads";
import { SEGMENTOS_PRINCIPALES, origenDe } from "../../data/segmentos";
import WhatsAppCta from "./WhatsAppCta";

const emptyForm = { name: "", whatsapp: "", email: "", pack: "", rubro: "", cantidad: "" };

/*
  La pregunta de horas.

  Es el unico dato que decide el precio: una hora y cuatro horas son dos
  trabajos distintos. Preguntarlo aqui convierte un lead en una cotizacion.

  Cada servicio la redacta a su manera en `preguntaVolumen` (segmentos.js). Si
  no hay segmento (formulario de /contacto) se pregunta en generico.
*/
const PREGUNTA_HORAS = "¿Cuántas horas necesitas?";

const fieldClass =
  "w-full rounded-xl border border-navy-200 bg-white px-4 py-3 text-navy-900 outline-none transition focus:border-navy-500";
const labelClass = "mb-2 block text-sm font-semibold text-navy-900";

const FotoLeadForm = ({ segmento }) => {
  const [formData, setFormData] = useState(emptyForm);
  const [status, setStatus] = useState({ type: null, message: "" });
  const [isSubmitting, setIsSubmitting] = useState(false);

  /* En /contacto no se sabe a cual de los dos servicios viene el visitante,
     asi que se pregunta. En la pagina de un servicio ya se sabe y el campo no
     aparece. */
  const servicioOptions = segmento ? [] : SEGMENTOS_PRINCIPALES.map((item) => item.nombre);

  /* Con tres packs publicados, el formulario pregunta cuál: es lo que
     convierte el lead en una reserva y no en una consulta. */
  const packOptions =
    segmento && segmento.packs.length > 1
      ? segmento.packs.map((pack) => ({
          value: pack.nombre,
          label: `${pack.nombre} — ${pack.volumen}`,
        }))
      : [];

  /* La unidad del servicio, no "hora" fija: en estudio se pregunta por
     personas, y guardar "3 hora" en el lead es un dato falso. */
  const unidad = segmento?.unidad === "sesión" ? "persona" : "hora";
  const preguntaVolumen = segmento?.preguntaVolumen || PREGUNTA_HORAS;

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormData((current) => ({ ...current, [name]: value }));
    if (status.type === "error") setStatus({ type: null, message: "" });
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    const error = validateLead(formData);
    if (error) {
      setStatus({ type: "error", message: error });
      return;
    }

    setIsSubmitting(true);
    setStatus({ type: null, message: "" });

    try {
      await submitLead({
        ...formData,
        origen: segmento ? origenDe(segmento.id) : "foto-consulta",
        pack: formData.pack || (segmento && !packOptions.length ? segmento.packs[0].nombre : ""),
        rubro: formData.rubro || segmento?.nombre || "",
        unidad: formData.cantidad ? unidad : "",
      });
      trackFormLead({ form: "fotografia", pack: formData.pack || "sin_pack" });
      setStatus({
        type: "success",
        message: "Te escribimos por WhatsApp para coordinar la fecha y las horas.",
      });
      setFormData(emptyForm);
    } catch (err) {
      console.error("Error al enviar el lead de fotografía:", err);
      setStatus({
        type: "error",
        message: "No pudimos enviar el formulario. Escríbenos directo por WhatsApp.",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (status.type === "success") {
    return (
      <div>
        <span className="flex h-14 w-14 items-center justify-center rounded-full bg-sand-100">
          <CheckCircle className="h-7 w-7 text-navy-800" aria-hidden="true" />
        </span>
        <h3 className="mt-5 font-display text-2xl font-bold tracking-tight text-navy-900">
          Recibimos tus datos.
        </h3>
        <p className="mt-3 max-w-sm leading-relaxed text-navy-600">{status.message}</p>
        <WhatsAppCta
          message={segmento?.whatsapp || "Hola Omega, acabo de dejar mis datos en la web."}
          location="form_success"
          variant="dark"
          className="mt-6 w-full"
        >
          Seguir por WhatsApp
        </WhatsAppCta>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-5" noValidate>
      <div className="grid gap-5 sm:grid-cols-2">
        <label className="block">
          <span className={labelClass}>Nombre</span>
          <input
            type="text"
            name="name"
            value={formData.name}
            onChange={handleChange}
            className={fieldClass}
            placeholder="Tu nombre"
            autoComplete="name"
            required
          />
        </label>
        <label className="block">
          <span className={labelClass}>WhatsApp</span>
          <input
            type="tel"
            name="whatsapp"
            value={formData.whatsapp}
            onChange={handleChange}
            className={fieldClass}
            placeholder="+56 9 1234 5678"
            autoComplete="tel"
            required
          />
        </label>
      </div>

      <label className="block">
        <span className={labelClass}>Correo</span>
        <input
          type="email"
          name="email"
          value={formData.email}
          onChange={handleChange}
          className={fieldClass}
          placeholder="tu@correo.cl"
          autoComplete="email"
          required
        />
      </label>

      <div className="grid gap-5 sm:grid-cols-2">
        {servicioOptions.length ? (
          <label className="block">
            <span className={labelClass}>¿Qué necesitas?</span>
            <select name="pack" value={formData.pack} onChange={handleChange} className={fieldClass}>
              <option value="">Selecciona</option>
              {servicioOptions.map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </select>
          </label>
        ) : null}
        {packOptions.length ? (
          <label className="block">
            <span className={labelClass}>
              Pack <span className="font-normal text-navy-400">(opcional)</span>
            </span>
            <select name="pack" value={formData.pack} onChange={handleChange} className={fieldClass}>
              <option value="">Aún no lo decido</option>
              {packOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </label>
        ) : null}
        {preguntaVolumen && !packOptions.length ? (
          <label className="block">
            <span className={labelClass}>{preguntaVolumen}</span>
            <input
              type="number"
              name="cantidad"
              min="1"
              step="1"
              inputMode="numeric"
              value={formData.cantidad}
              onChange={handleChange}
              className={fieldClass}
              placeholder="Un número aproximado basta"
            />
          </label>
        ) : null}
        <label className="block">
          <span className={labelClass}>
            Qué vas a fotografiar <span className="font-normal text-navy-400">(opcional)</span>
          </span>
          <input
            type="text"
            name="rubro"
            value={formData.rubro}
            onChange={handleChange}
            className={fieldClass}
            placeholder={segmento?.paraQuien || "Cumpleaños, retratos, evento de empresa…"}
          />
        </label>
      </div>

      {status.type === "error" && (
        <div className="flex items-start gap-3 rounded-xl border border-red-200 bg-red-50 p-3" role="alert">
          <AlertCircle className="mt-0.5 h-5 w-5 shrink-0 text-red-600" aria-hidden="true" />
          <p className="text-sm font-medium text-red-800">{status.message}</p>
        </div>
      )}

      <button type="submit" disabled={isSubmitting} className="btn-primary w-full">
        {isSubmitting ? (
          <>
            <Loader className="h-4 w-4 animate-spin" aria-hidden="true" />
            Enviando…
          </>
        ) : (
          <>
            <Send className="h-4 w-4" aria-hidden="true" />
            Pedir fecha
          </>
        )}
      </button>

      <p className="text-center text-xs leading-relaxed text-navy-500">
        Solo para coordinar el trabajo. Sin newsletter.
      </p>
    </form>
  );
};

export default FotoLeadForm;
